import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Icon } from "@/components/landing/Icon";
import { Reveal, staggerContainer, staggerItem } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { ChildCategories } from "@/components/landing/ChildCategories";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

const SUBCATEGORY_ORDER = ["Corporate Compliance", "Payroll & Labour Compliance", "Compliance Management & Advisory"];
const slugify = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function ServiceCard({ s, i }) {
  return (
    <motion.div variants={staggerItem}>
      <Link
        to={`/compliance/${s.slug}`}
        data-testid={`compliance-card-${s.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_20px_44px_rgba(15,30,60,0.12)]"
      >
        <div className="relative h-48 overflow-hidden">
          <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold backdrop-blur ${i % 2 === 0 ? "bg-navy-900/90 text-gold-400" : "bg-emerald2-600/95 text-white"}`}>
            {s.sub_category || s.category}
          </span>
          <span className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-xl bg-white/95 text-navy-900 shadow-[0_6px_16px_rgba(15,30,60,0.18)]">
            <Icon name={s.icon} className="h-4 w-4" />
          </span>
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="text-lg font-semibold leading-snug text-navy-900 transition-colors group-hover:text-gold-600">{s.title}</h3>
          <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-slate-500">{s.subtitle}</p>
          <div className="mt-auto flex items-center justify-between pt-5">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Clock className="h-3.5 w-3.5" /> {s.read_time}
            </div>
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald2-600">
              View details <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Compliance() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .listServices("Compliance")
      .then(({ services }) => setServices(services || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  const groups = SUBCATEGORY_ORDER.map((sub) => ({
    sub,
    items: services.filter((s) => s.sub_category === sub),
  })).filter((g) => g.items.length);

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="compliance-page">
      <SEO
        title="Annual ROC Compliance & Corporate Governance Services | Biz360 Compliance"
        description="Stay statutory compliant with MCA filings (AOC-4, MGT-7), DIN eKYC, DIR-3, PF & ESIC returns, and Maharashtra Professional Tax (PTRC/PTEC)."
        canonical="/compliance"
        keywords="ROC compliance Pune, annual company filing MCA, AOC-4 MGT-7 Mumbai, DIN KYC, labour law compliance Maharashtra, PF ESIC return"
        breadcrumbs={[{ label: "Compliance", href: "/compliance" }]}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="compliance-breadcrumb">
                <Link to="/" className="hover:text-navy-800">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Compliance</span>
              </div>
            </Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Reveal>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Compliance
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">
                  Stay compliant, without the chase
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-4 text-base text-slate-600 md:text-lg">
                  Corporate, payroll and advisory compliance — tracked, filed and managed on one calendar.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            {loading ? (
              <p className="py-16 text-center text-slate-500">Loading services...</p>
            ) : (
              groups.map((group) => (
                <div key={group.sub} id={slugify(group.sub)} className="scroll-mt-28 [&:not(:first-child)]:mt-16">
                  <Reveal>
                    <h2 className="text-2xl font-semibold tracking-tight text-navy-900 md:text-3xl">{group.sub}</h2>
                  </Reveal>
                  <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: false, margin: "-60px" }}
                    className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    {group.items.map((s, i) => (
                      <ServiceCard key={s.slug} s={s} i={i} />
                    ))}
                  </motion.div>
                </div>
              ))
            )}
          </div>
        </section>

        <ChildCategories parentHref="/compliance" parentLabel="Compliance" />
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
